import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DataGrid, GridPagination } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { beautifyNumber } from '../../../Utils/FormatNumber.js';
import DocumentService from '../../../Services/documents.service.js';
import Constants from '../../../assests/Constants/constants.js';
import { useFontSize } from '../../../Contexts/FontSizeContext';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import {
  downloadTransactionSummaryExcel,
  downloadTransactionSummaryPdf,
} from '../../../Utils/transactionSummariesExport.js';
import MainStyles from '../../../pages/Main/Main.module.scss';
import styles from './TransactionSummariesTab.module.scss';

/** Tax invoice, credit tax invoice and invoice-receipt — the documents that record a sale. */
const DEFAULT_SUMMARY_TYPES = [4, 5, 6];
const CREDIT_TAX_INVOICE_TYPE = 5;

const ROW_HEIGHT_BY_FONT = {
  small: 34,
  medium: 40,
  large: 48,
};

// Same look as the clients table on the main page (ClientsDataGrid).
const StyledDataGrid = styled(DataGrid)(() => ({
  width: '100%',
  border: '1px solid #E5E7EB',
  borderRadius: '10px',
  backgroundColor: '#FFFFFF',
  fontFamily: 'Montserrat, sans-serif',
  '& *': {
    boxSizing: 'border-box !important',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#e3e7fe !important',
    borderBottom: '1px solid #E5E7EB',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#e3e7fe !important',
    padding: '0 6px',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#1222b3',
    textAlign: 'center',
    fontSize: 'var(--xtram-grid-header-font, 13px)',
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    justifyContent: 'center',
  },
  '& .MuiDataGrid-iconButtonContainer': {
    visibility: 'visible',
    width: 'auto',
  },
  '& .MuiDataGrid-sortIcon': {
    opacity: '1 !important',
    color: '#2532ae',
  },
  '& .MuiDataGrid-columnSeparator': {
    color: '#E5E7EB',
  },
  '& .MuiDataGrid-row': {
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #EEF1F5',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#F8FAFC !important',
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderInlineEnd: '1px solid #F1F5F9',
    color: '#0f1438',
    fontWeight: 500,
    fontSize: 'var(--xtram-grid-cell-font, 13px)',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-virtualScroller': {
    backgroundColor: '#FFFFFF',
    scrollbarWidth: 'thin',
    scrollbarColor: '#d8daf2 transparent',
  },
  '& .MuiDataGrid-scrollbar.MuiDataGrid-scrollbar--horizontal': {
    display: 'block !important',
  },
}));

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;
const round3 = (value) => Math.round((Number(value) || 0) * 1000) / 1000;
const formatQuantity = (value) => Number(value || 0).toLocaleString('he-IL', { maximumFractionDigits: 3 });
const formatMoney = (value) => `₪ ${beautifyNumber(value)}`;

const itemKeyOf = (line) => {
  const makat = String(line.makat ?? '').trim().toLowerCase();
  if (makat) return `m:${makat}`;
  const name = String(line.name ?? '').trim().toLowerCase();
  if (name) return `n:${name}`;
  return `i:${line.itemId || ''}`;
};

const clientKeyOf = (doc) => String(doc.accountCodeId ?? doc.accountCodeName ?? '');
const docKeyOf = (id) => String(id).toLowerCase();

/** Credit invoices store a negative total while their line prices stay positive. */
const documentSign = (doc) =>
  Number(doc.transactionDocumentType) === CREDIT_TAX_INVOICE_TYPE || Number(doc.amount) < 0 ? -1 : 1;

const StatCard = ({ label, value, hint, icon, tone }) => (
  <div className={`${styles.statCard} ${styles[tone]}`}>
    <div className={styles.statText}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{value}</span>
      {hint && <span className={styles.statHint}>{hint}</span>}
    </div>
    <span className={styles.statIcon}>{icon}</span>
  </div>
);

const TotalsFooter = ({ title, items = [] }) => (
  <div className={styles.footer}>
    <div className={styles.footerTotals}>
      <span className={styles.footerTitle}>{title}</span>
      {items.map((item) => (
        <span key={item.label} className={styles.footerItem}>
          <span>{item.label}:</span>
          <strong className={Number(item.raw) < 0 ? styles.negative : undefined}>{item.value}</strong>
        </span>
      ))}
    </div>
    <GridPagination />
  </div>
);

const TransactionSummariesTab = ({
  client,
  token,
  direction,
  documents,
  periodLabel,
  documentTypeOptions,
}) => {
  const { t } = useTranslation();
  const { fontSize } = useFontSize();
  const rowHeight = ROW_HEIGHT_BY_FONT[fontSize] ?? ROW_HEIGHT_BY_FONT.medium;
  const [view, setView] = useState('items');
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [documentTypes, setDocumentTypes] = useState(DEFAULT_SUMMARY_TYPES);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [includeCanceled, setIncludeCanceled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Linked documents from other months are only returned to complete a family in the grid.
  const periodDocuments = useMemo(
    () => (documents || []).filter((doc) => !doc?.isOutOfPeriod && doc?.id),
    [documents]
  );

  const periodDocumentIdsKey = useMemo(
    () => periodDocuments.map((doc) => String(doc.id)).sort().join(','),
    [periodDocuments]
  );

  useEffect(() => {
    if (!client?.caseId || !token) return undefined;
    const ids = periodDocumentIdsKey ? periodDocumentIdsKey.split(',') : [];
    if (ids.length === 0) {
      setLines([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    DocumentService.GetDocumentsItemsSummary(client.caseId, ids, token)
      .then((result) => {
        if (!cancelled) setLines(result?.lines || result?.Lines || []);
      })
      .catch((error) => {
        console.error('Error loading documents items summary:', error);
        if (!cancelled) {
          setLines([]);
          toast.error(t('summaryLoadError'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [client?.caseId, token, periodDocumentIdsKey, t]);

  const eligibleDocuments = useMemo(
    () => periodDocuments.filter((doc) => {
      if (documentTypes.length > 0 && !documentTypes.includes(Number(doc.transactionDocumentType))) return false;
      if (!includeCanceled && doc.isCanceled) return false;
      return true;
    }),
    [periodDocuments, documentTypes, includeCanceled]
  );

  const clientOptions = useMemo(() => {
    const seen = new Map();
    eligibleDocuments.forEach((doc) => {
      const key = clientKeyOf(doc);
      if (!key || seen.has(key)) return;
      const name = doc.accountCodeName || t('summaryNoClient');
      seen.set(key, { value: key, label: doc.accountCaseId ? `${name} - ${doc.accountCaseId}` : name });
    });
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [eligibleDocuments, t]);

  /** Each line signed by its document and carrying its share of the document's net and gross amounts. */
  const enrichedLines = useMemo(() => {
    const docsById = new Map(eligibleDocuments.map((doc) => [docKeyOf(doc.id), doc]));
    const linesByDoc = new Map();
    lines.forEach((line) => {
      const docId = docKeyOf(line.documentId);
      if (!docsById.has(docId)) return;
      if (!linesByDoc.has(docId)) linesByDoc.set(docId, []);
      linesByDoc.get(docId).push(line);
    });

    const result = [];
    linesByDoc.forEach((docLines, docId) => {
      const doc = docsById.get(docId);
      const sign = documentSign(doc);
      const docLinesTotal = docLines.reduce((sum, line) => sum + Math.abs(Number(line.lineTotal) || 0), 0);
      const docNet = Math.abs(Number(doc.netAmount) || 0);
      const docGross = Math.abs(Number(doc.amount) || 0);
      docLines.forEach((line) => {
        const quantity = Number(line.quantity) || 0;
        const price = Number(line.price) || 0;
        const lineTotal = Math.abs(Number(line.lineTotal) || 0);
        const share = docLinesTotal > 0 ? lineTotal / docLinesTotal : 0;
        result.push({
          ...line,
          doc,
          docKey: docId,
          itemKey: itemKeyOf(line),
          clientKey: clientKeyOf(doc),
          quantity: sign * Math.abs(quantity),
          beforeDiscount: sign * Math.abs(quantity * price),
          afterDiscount: sign * lineTotal,
          net: sign * (docLinesTotal > 0 ? docNet * share : lineTotal),
          gross: sign * (docLinesTotal > 0 ? docGross * share : lineTotal),
        });
      });
    });
    return result;
  }, [lines, eligibleDocuments]);

  const itemOptions = useMemo(() => {
    const seen = new Map();
    enrichedLines.forEach((line) => {
      if (seen.has(line.itemKey)) return;
      const name = line.name || t('summaryNoItemName');
      seen.set(line.itemKey, { value: line.itemKey, label: line.makat ? `${line.makat} - ${name}` : name });
    });
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [enrichedLines, t]);

  const filteredLines = useMemo(
    () => enrichedLines.filter((line) => {
      if (selectedClients.length > 0 && !selectedClients.includes(line.clientKey)) return false;
      if (selectedItems.length > 0 && !selectedItems.includes(line.itemKey)) return false;
      return true;
    }),
    [enrichedLines, selectedClients, selectedItems]
  );

  /**
   * The documents in scope with the amounts they contribute. Filtering by item narrows each
   * document to its matching lines; otherwise whole documents count, including the ones issued
   * without item lines.
   */
  const scopeDocuments = useMemo(() => {
    const byDoc = new Map();
    if (selectedItems.length > 0) {
      filteredLines.forEach((line) => {
        let entry = byDoc.get(line.docKey);
        if (!entry) {
          entry = { doc: line.doc, net: 0, gross: 0, quantity: 0 };
          byDoc.set(line.docKey, entry);
        }
        entry.net += line.net;
        entry.gross += line.gross;
        entry.quantity += line.quantity;
      });
      return [...byDoc.values()];
    }

    const quantityByDoc = new Map();
    enrichedLines.forEach((line) => {
      quantityByDoc.set(line.docKey, (quantityByDoc.get(line.docKey) || 0) + line.quantity);
    });
    return eligibleDocuments
      .filter((doc) => selectedClients.length === 0 || selectedClients.includes(clientKeyOf(doc)))
      .map((doc) => ({
        doc,
        net: Number(doc.netAmount) || 0,
        gross: Number(doc.amount) || 0,
        quantity: quantityByDoc.get(docKeyOf(doc.id)) || 0,
      }));
  }, [filteredLines, enrichedLines, eligibleDocuments, selectedClients, selectedItems]);

  const summary = useMemo(() => {
    const result = {
      documentsCount: scopeDocuments.length,
      net: 0,
      gross: 0,
      credits: 0,
      creditsCount: 0,
      clients: new Set(),
      byType: new Map(),
    };
    scopeDocuments.forEach((entry) => {
      result.net += entry.net;
      result.gross += entry.gross;
      if (entry.gross < 0) {
        result.credits += entry.gross;
        result.creditsCount += 1;
      }
      result.clients.add(clientKeyOf(entry.doc));
      const type = Number(entry.doc.transactionDocumentType);
      const typeEntry = result.byType.get(type) || { type, count: 0, gross: 0 };
      typeEntry.count += 1;
      typeEntry.gross += entry.gross;
      result.byType.set(type, typeEntry);
    });
    return {
      documentsCount: result.documentsCount,
      net: round2(result.net),
      vat: round2(result.gross - result.net),
      gross: round2(result.gross),
      credits: round2(result.credits),
      creditsCount: result.creditsCount,
      average: result.documentsCount ? round2(result.gross / result.documentsCount) : 0,
      clientsCount: result.clients.size,
      itemsCount: new Set(filteredLines.map((line) => line.itemKey)).size,
      quantity: round3(filteredLines.reduce((sum, line) => sum + line.quantity, 0)),
      byType: [...result.byType.values()]
        .map((entry) => ({ ...entry, gross: round2(entry.gross) }))
        .sort((a, b) => a.type - b.type),
    };
  }, [scopeDocuments, filteredLines]);

  const itemRows = useMemo(() => {
    const groups = new Map();
    filteredLines.forEach((line) => {
      let group = groups.get(line.itemKey);
      if (!group) {
        group = {
          id: line.itemKey,
          makat: line.makat || '',
          name: line.name || t('summaryNoItemName'),
          documentIds: new Set(),
          quantity: 0,
          beforeDiscount: 0,
          afterDiscount: 0,
          net: 0,
        };
        groups.set(line.itemKey, group);
      }
      group.documentIds.add(line.docKey);
      group.quantity += line.quantity;
      group.beforeDiscount += line.beforeDiscount;
      group.afterDiscount += line.afterDiscount;
      group.net += line.net;
    });
    return [...groups.values()].map((group) => ({
      id: group.id,
      makat: group.makat,
      name: group.name,
      documentsCount: group.documentIds.size,
      quantity: round3(group.quantity),
      averagePrice: group.quantity ? round2(group.afterDiscount / group.quantity) : 0,
      discount: round2(group.beforeDiscount - group.afterDiscount),
      afterDiscount: round2(group.afterDiscount),
      net: round2(group.net),
    }));
  }, [filteredLines, t]);

  const clientRows = useMemo(() => {
    const groups = new Map();
    scopeDocuments.forEach((entry) => {
      const key = clientKeyOf(entry.doc);
      let group = groups.get(key);
      if (!group) {
        group = {
          id: key || '—',
          cardId: entry.doc.accountCodeId ?? '',
          accountCaseId: entry.doc.accountCaseId || '',
          name: entry.doc.accountCodeName || t('summaryNoClient'),
          documentsCount: 0,
          quantity: 0,
          net: 0,
          total: 0,
        };
        groups.set(key, group);
      }
      group.documentsCount += 1;
      group.quantity += entry.quantity;
      group.net += entry.net;
      group.total += entry.gross;
    });
    return [...groups.values()].map((group) => ({
      ...group,
      quantity: round3(group.quantity),
      net: round2(group.net),
      vat: round2(group.total - group.net),
      total: round2(group.total),
      averageDocument: group.documentsCount ? round2(group.total / group.documentsCount) : 0,
    }));
  }, [scopeDocuments, t]);

  const visibleRows = useMemo(() => {
    const source = view === 'items' ? itemRows : clientRows;
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? source.filter((row) =>
        [row.makat, row.name, row.cardId, row.accountCaseId]
          .filter((v) => v != null && v !== '')
          .some((v) => String(v).toLowerCase().includes(q)))
      : source;
    const absTotal = filtered.reduce((sum, row) => sum + Math.abs(Number(row.net) || 0), 0);
    return filtered
      .map((row) => ({ ...row, share: absTotal ? (Math.abs(Number(row.net) || 0) / absTotal) * 100 : 0 }))
      .sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
  }, [view, itemRows, clientRows, searchQuery]);

  const totals = useMemo(() => {
    const sum = (field) => round2(visibleRows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0));
    return {
      documentsCount: view === 'items'
        ? new Set(filteredLines.map((line) => line.docKey)).size
        : visibleRows.reduce((acc, row) => acc + (row.documentsCount || 0), 0),
      quantity: round3(visibleRows.reduce((acc, row) => acc + (Number(row.quantity) || 0), 0)),
      discount: sum('discount'),
      afterDiscount: sum('afterDiscount'),
      net: sum('net'),
      vat: sum('vat'),
      total: sum('total'),
    };
  }, [visibleRows, view, filteredLines]);

  const amountCell = (params) => (
    <span className={Number(params.value) < 0 ? styles.negative : undefined}>{beautifyNumber(params.value)}</span>
  );

  const quantityCell = (params) => (
    <span className={Number(params.value) < 0 ? styles.negative : undefined}>{formatQuantity(params.value)}</span>
  );

  const shareCell = (params) => (
    <div className={styles.shareCell}>
      <div className={styles.shareTrack}>
        <div className={styles.shareFill} style={{ width: `${Math.min(100, params.value || 0)}%` }} />
      </div>
      <span className={styles.shareValue}>{`${(params.value || 0).toFixed(1)}%`}</span>
    </div>
  );

  const textCol = (field, headerName, flex, minWidth) => ({
    field,
    headerName,
    headerAlign: 'center',
    align: 'center',
    flex,
    minWidth,
  });

  const numberCol = (field, headerName, renderCell = amountCell, minWidth = 100) => ({
    field,
    headerName,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    flex: 0.8,
    minWidth,
    renderCell,
  });

  const shareCol = { ...numberCol('share', t('summaryShare'), shareCell, 130), flex: 1 };

  const columns = view === 'items'
    ? [
      textCol('makat', t('Makat'), 0.7, 90),
      textCol('name', t('summaryItemName'), 1.6, 160),
      numberCol('documentsCount', t('summaryDocumentsCount'), (params) => params.value, 90),
      numberCol('quantity', t('summaryTotalQuantity'), quantityCell),
      numberCol('averagePrice', t('summaryAveragePrice')),
      numberCol('discount', t('summaryDiscount')),
      numberCol('afterDiscount', t('summaryTotalAfterDiscount')),
      numberCol('net', t('summaryNetBeforeVat')),
      shareCol,
    ]
    : [
      textCol('cardId', t('summaryCardId'), 0.7, 90),
      textCol('name', t('Client Name'), 1.6, 160),
      textCol('accountCaseId', t('CompanyId'), 0.9, 110),
      numberCol('documentsCount', t('summaryDocumentsCount'), (params) => params.value, 90),
      numberCol('quantity', t('summaryTotalQuantity'), quantityCell),
      numberCol('net', t('summaryNetBeforeVat')),
      numberCol('vat', t('VATAmount')),
      numberCol('total', t('summaryTotalWithVat')),
      numberCol('averageDocument', t('summaryAverageDocument')),
      shareCol,
    ];

  const footerItems = view === 'items'
    ? [
      { label: t('summaryDocumentsCount'), value: totals.documentsCount },
      { label: t('summaryTotalQuantity'), value: formatQuantity(totals.quantity), raw: totals.quantity },
      { label: t('summaryDiscount'), value: beautifyNumber(totals.discount), raw: totals.discount },
      { label: t('summaryTotalAfterDiscount'), value: beautifyNumber(totals.afterDiscount), raw: totals.afterDiscount },
      { label: t('summaryNetBeforeVat'), value: beautifyNumber(totals.net), raw: totals.net },
    ]
    : [
      { label: t('summaryDocumentsCount'), value: totals.documentsCount },
      { label: t('summaryNetBeforeVat'), value: beautifyNumber(totals.net), raw: totals.net },
      { label: t('VATAmount'), value: beautifyNumber(totals.vat), raw: totals.vat },
      { label: t('summaryTotalWithVat'), value: beautifyNumber(totals.total), raw: totals.total },
    ];

  const cards = [
    {
      tone: 'purple',
      label: t('summaryTotalDocuments'),
      value: beautifyNumber(summary.documentsCount, 0, true),
      icon: <DescriptionOutlinedIcon />,
    },
    {
      tone: 'blue',
      label: t('summaryTotalWithVat'),
      value: formatMoney(summary.gross),
      icon: <AccountBalanceWalletOutlinedIcon />,
    },
    {
      tone: 'green',
      label: t('summaryNetBeforeVat'),
      value: formatMoney(summary.net),
      icon: <ReceiptLongOutlinedIcon />,
    },
    {
      tone: 'orange',
      label: t('VATAmount'),
      value: formatMoney(summary.vat),
      icon: <PercentOutlinedIcon />,
    },
    {
      tone: 'red',
      label: t('summaryCredits'),
      value: formatMoney(summary.credits),
      hint: `${summary.creditsCount} ${t('summaryDocumentsCount')}`,
      icon: <AssignmentReturnOutlinedIcon />,
    },
    {
      tone: 'slate',
      label: t('summaryAverageDocument'),
      value: formatMoney(summary.average),
      icon: <QueryStatsOutlinedIcon />,
    },
    {
      tone: 'teal',
      label: t('summaryClientsCount'),
      value: beautifyNumber(summary.clientsCount, 0, true),
      icon: <PeopleOutlineIcon />,
    },
    {
      tone: 'purple',
      label: t('summaryItemsCount'),
      value: beautifyNumber(summary.itemsCount, 0, true),
      hint: `${t('summaryTotalQuantity')}: ${formatQuantity(summary.quantity)}`,
      icon: <Inventory2OutlinedIcon />,
    },
  ];

  const buildReport = () => {
    const filtersText = [
      periodLabel ? `${t('Period')}: ${periodLabel}` : '',
      documentTypes.length > 0
        ? documentTypes.map((type) => Constants.TransactionDocuments[type]).filter(Boolean).join(', ')
        : '',
      selectedClients.length > 0
        ? `${t('summaryClientsFilter')}: ${clientOptions.filter((o) => selectedClients.includes(o.value)).map((o) => o.label).join(', ')}`
        : '',
      selectedItems.length > 0
        ? `${t('summaryItemsFilter')}: ${itemOptions.filter((o) => selectedItems.includes(o.value)).map((o) => o.label).join(', ')}`
        : '',
    ].filter(Boolean).join(' · ');

    const summaryItems = [
      { label: t('summaryTotalDocuments'), value: String(summary.documentsCount) },
      { label: t('summaryNetBeforeVat'), value: beautifyNumber(summary.net) },
      { label: t('VATAmount'), value: beautifyNumber(summary.vat) },
      { label: t('summaryTotalWithVat'), value: beautifyNumber(summary.gross) },
      { label: t('summaryCredits'), value: beautifyNumber(summary.credits) },
      { label: t('summaryClientsCount'), value: String(summary.clientsCount) },
    ];
    const typeBreakdown = summary.byType.map((entry) => ({
      label: Constants.TransactionDocuments[entry.type] || String(entry.type),
      value: `${entry.count} · ${beautifyNumber(entry.gross)}`,
    }));

    if (view === 'items') {
      return {
        title: t('summarySalesByItemTitle'),
        subtitle: filtersText,
        summaryItems,
        typeBreakdown,
        columns: [
          { label: t('Makat'), width: 14 },
          { label: t('summaryItemName'), width: 32 },
          { label: t('summaryDocumentsCount'), width: 12 },
          { label: t('summaryTotalQuantity'), numeric: true, width: 14 },
          { label: t('summaryAveragePrice'), numeric: true },
          { label: t('summaryDiscount'), numeric: true },
          { label: t('summaryTotalAfterDiscount'), numeric: true },
          { label: t('summaryNetBeforeVat'), numeric: true },
          { label: t('summaryShare'), numeric: true, width: 12 },
        ],
        rows: visibleRows.map((row) => [
          row.makat, row.name, row.documentsCount, row.quantity,
          row.averagePrice, row.discount, row.afterDiscount, row.net, round2(row.share),
        ]),
        totalRow: [t('Total'), '', totals.documentsCount, totals.quantity, '', totals.discount, totals.afterDiscount, totals.net, 100],
      };
    }
    return {
      title: t('summarySalesByClientTitle'),
      subtitle: filtersText,
      summaryItems,
      typeBreakdown,
      columns: [
        { label: t('summaryCardId'), width: 14 },
        { label: t('Client Name'), width: 32 },
        { label: t('CompanyId'), width: 16 },
        { label: t('summaryDocumentsCount'), width: 12 },
        { label: t('summaryTotalQuantity'), numeric: true, width: 14 },
        { label: t('summaryNetBeforeVat'), numeric: true },
        { label: t('VATAmount'), numeric: true },
        { label: t('summaryTotalWithVat'), numeric: true },
        { label: t('summaryAverageDocument'), numeric: true },
        { label: t('summaryShare'), numeric: true, width: 12 },
      ],
      rows: visibleRows.map((row) => [
        row.cardId, row.name, row.accountCaseId, row.documentsCount,
        row.quantity, row.net, row.vat, row.total, row.averageDocument, round2(row.share),
      ]),
      totalRow: [t('Total'), '', '', totals.documentsCount, totals.quantity, totals.net, totals.vat, totals.total, '', 100],
    };
  };

  const handleExport = async (format) => {
    if (visibleRows.length === 0) {
      toast.info(t('NoDataToDownload'));
      return;
    }
    const options = {
      report: buildReport(),
      clientName: client?.name,
      clientCase: client?.caseId,
      fileSuffix: view === 'items' ? 'SalesByItem' : 'SalesByClient',
      direction,
    };
    setExporting(true);
    try {
      if (format === 'pdf') {
        await downloadTransactionSummaryPdf({ ...options, token });
      } else {
        downloadTransactionSummaryExcel(options);
      }
      toast.success(t('summaryExportSuccess'));
    } catch (error) {
      console.error('Summary export failed:', error);
      toast.error(t('summaryExportFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={styles.root} dir={direction}>
      <div className={styles.cards}>
        {cards.map((card) => (
          <StatCard key={`${card.tone}-${card.label}`} {...card} />
        ))}
      </div>

      {summary.byType.length > 0 && (
        <div className={styles.typeBreakdown}>
          {summary.byType.map((entry) => (
            <span key={entry.type} className={styles.typeChip}>
              <span className={styles.typeChipName}>{Constants.TransactionDocuments[entry.type] || entry.type}</span>
              <span className={styles.typeChipCount}>{entry.count}</span>
              <span className={`${styles.typeChipAmount} ${entry.gross < 0 ? styles.negative : ''}`}>
                {formatMoney(entry.gross)}
              </span>
            </span>
          ))}
        </div>
      )}

      <div className={`${MainStyles.filtersBarMainPage} ${MainStyles[direction]} ${styles.filtersBar}`}>
        <TextField
          size="small"
          fullWidth
          dir={direction}
          placeholder={t('summarySearchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={MainStyles['search-bar']}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
            sx: {
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              '& .MuiInputAdornment-root': { height: 'auto', maxHeight: 'none' },
              '& input': { padding: 0, height: 'auto' },
            },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={MainStyles['search-icon']} />
              </InputAdornment>
            ),
          }}
        />
        <div className={MainStyles.filtersSelect}>
          <AutocompleteSelectFilter
            options={documentTypeOptions}
            selectedValues={documentTypeOptions.filter((option) => documentTypes.includes(option.value))}
            onChange={(event, newValues) => setDocumentTypes((newValues || []).map((item) => item.value))}
            label={t('ChooseDocumentType')}
            placeholder={t('ChooseDocumentType')}
            isMultiple
            direction={direction}
            portalMenu
          />
        </div>
        <div className={MainStyles.filtersSelect}>
          <AutocompleteSelectFilter
            options={clientOptions}
            selectedValues={clientOptions.filter((option) => selectedClients.includes(option.value))}
            onChange={(event, newValues) => setSelectedClients((newValues || []).map((item) => item.value))}
            label={t('summaryClientsFilter')}
            placeholder={t('summaryClientsFilter')}
            isMultiple
            searchable
            direction={direction}
            portalMenu
          />
        </div>
        <div className={MainStyles.filtersSelect}>
          <AutocompleteSelectFilter
            options={itemOptions}
            selectedValues={itemOptions.filter((option) => selectedItems.includes(option.value))}
            onChange={(event, newValues) => setSelectedItems((newValues || []).map((item) => item.value))}
            label={t('summaryItemsFilter')}
            placeholder={t('summaryItemsFilter')}
            isMultiple
            searchable
            direction={direction}
            portalMenu
          />
        </div>
        <FormControlLabel
          className={styles.canceledToggle}
          control={(
            <Checkbox
              size="small"
              checked={includeCanceled}
              onChange={(e) => setIncludeCanceled(e.target.checked)}
              sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
            />
          )}
          label={t('summaryIncludeCanceled')}
        />
      </div>

      <div className={`${MainStyles['export-buttons']} ${MainStyles[direction]}`} dir={direction}>
        <div className={MainStyles.tableViewGroup}>
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_, next) => next && setView(next)}
            className={styles.viewToggle}
          >
            <ToggleButton value="items">{t('summaryByItems')}</ToggleButton>
            <ToggleButton value="clients">{t('summaryByClients')}</ToggleButton>
          </ToggleButtonGroup>
          {periodLabel && <span className={styles.periodLabel}>{`${t('Period')}: ${periodLabel}`}</span>}
        </div>
        <Chip
          className={`${MainStyles.exportChip} ${MainStyles.exportChipPdf}`}
          icon={<PictureAsPdfIcon />}
          label={t('summaryExportPdf')}
          onClick={() => handleExport('pdf')}
          disabled={loading || exporting}
          clickable
          variant="outlined"
          dir={direction}
        />
        <Chip
          className={`${MainStyles.exportChip} ${MainStyles.exportChipExcel}`}
          icon={<TableChartIcon />}
          label={t('summaryExportExcel')}
          onClick={() => handleExport('excel')}
          disabled={loading || exporting}
          clickable
          variant="outlined"
          dir={direction}
        />
      </div>

      <div className={`${styles.gridWrap} ${direction || ''}`}>
        <StyledDataGrid
          key={view}
          rows={visibleRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          rowHeight={rowHeight}
          columnHeaderHeight={rowHeight}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          localeText={{ noRowsLabel: t('summaryNoData') }}
          slots={{ footer: TotalsFooter }}
          slotProps={{ footer: { title: t('Total'), items: footerItems } }}
        />
      </div>
    </div>
  );
};

export default TransactionSummariesTab;
